import {
  decimalsForPrecision,
  gramsToUnits,
  thcMgFromGrams,
  unitsToGrams,
} from '../units.js';
import type {
  BlendAllocation,
  BlendCalculationOutcome,
  BlendResult,
  ReachabilityRange,
} from '../types.js';
import type { WheelAcceptedResult } from './types.js';

/**
 * Build a Calculator deep-link for “Use This Result”.
 * Uses existing strainA/strainB params plus target and weight.
 * Blend transfers also carry accepted per-strain grams (gramsA / gramsB).
 */
export function buildCalculatorTransferPath(
  accepted: WheelAcceptedResult,
): string {
  const params = new URLSearchParams();
  if (accepted.kind === 'single') {
    params.set('strainA', accepted.strainId);
    params.set('target', String(accepted.thcMg));
    params.set('weight', String(accepted.sizeGrams));
  } else {
    params.set('strainA', accepted.primaryStrainId);
    params.set('strainB', accepted.balancingStrainId);
    params.set('target', String(accepted.thcMg));
    params.set('weight', String(accepted.totalWeightGrams));
    params.set('gramsA', String(accepted.primaryWeightGrams));
    params.set('gramsB', String(accepted.balancingWeightGrams));
  }
  return `/calculator?${params.toString()}`;
}

/** Parse optional numeric query params for calculator hydrate. */
export function parseCalculatorTransferParams(
  get: (key: string) => string | null,
): {
  strainIds: string[];
  target: string | null;
  weight: string | null;
  gramsA: number | null;
  gramsB: number | null;
} {
  const a = get('strainA');
  const b = get('strainB');
  const ids: string[] = [];
  if (a) ids.push(a);
  if (b && b !== a) ids.push(b);
  const target = get('target');
  const weight = get('weight');
  const gramsARaw = get('gramsA');
  const gramsBRaw = get('gramsB');
  const gramsA =
    gramsARaw != null && Number.isFinite(Number(gramsARaw))
      ? Number(gramsARaw)
      : null;
  const gramsB =
    gramsBRaw != null && Number.isFinite(Number(gramsBRaw))
      ? Number(gramsBRaw)
      : null;
  return {
    strainIds: ids,
    target: target && target.trim() !== '' ? target : null,
    weight: weight && weight.trim() !== '' ? weight : null,
    gramsA,
    gramsB,
  };
}

export type TransferBlendStrain = {
  id: string;
  name: string;
  producer?: string | undefined;
  thcPercent: number;
  grams: number;
};

/**
 * Build a calculator blend outcome from the wheel-accepted allocations.
 * Does not re-run the optimizer — preserves the accepted weights/THC.
 */
export function buildTransferredBlendOutcome(input: {
  primary: TransferBlendStrain;
  balancing: TransferBlendStrain;
  totalWeightGrams: number;
  targetThcMg: number;
  estimatedThcMg: number;
  scalePrecisionGrams: number;
}): BlendCalculationOutcome {
  const precision = input.scalePrecisionGrams;
  const decimals = decimalsForPrecision(precision);
  const strains = [input.primary, input.balancing];
  const allocations: BlendAllocation[] = strains.map((s, i) => {
    let units: number;
    try {
      units = gramsToUnits(s.grams, precision);
    } catch {
      units = Math.round(s.grams / precision);
    }
    const grams = unitsToGrams(units, precision);
    return {
      strainIndex: i,
      strainId: s.id,
      name: s.name,
      producer: s.producer,
      thcPercent: s.thcPercent,
      units,
      grams,
      thcMg: thcMgFromGrams(grams, s.thcPercent),
    };
  });

  const estimatedThcMg =
    Number.isFinite(input.estimatedThcMg)
      ? input.estimatedThcMg
      : allocations.reduce((sum, a) => sum + a.thcMg, 0);
  const differenceMg = estimatedThcMg - input.targetThcMg;
  const totalUnits = allocations.reduce((sum, a) => sum + a.units, 0);

  const reachability: ReachabilityRange = {
    minThcMg: Math.min(...allocations.map((a) => a.thcMg)),
    maxThcMg: Math.max(...allocations.map((a) => a.thcMg)),
    minStrainIndex: 0,
    maxStrainIndex: allocations.length > 1 ? 1 : 0,
  };

  // Reachability for display: use all-one-strain bounds from potencies.
  const allPrimary = thcMgFromGrams(
    input.totalWeightGrams,
    input.primary.thcPercent,
  );
  const allBalancing = thcMgFromGrams(
    input.totalWeightGrams,
    input.balancing.thcPercent,
  );
  reachability.minThcMg = Math.min(allPrimary, allBalancing);
  reachability.maxThcMg = Math.max(allPrimary, allBalancing);
  reachability.minStrainIndex = allPrimary <= allBalancing ? 0 : 1;
  reachability.maxStrainIndex = allPrimary >= allBalancing ? 0 : 1;

  const result: BlendResult = {
    allocations,
    totalWeightGrams: input.totalWeightGrams,
    totalUnits,
    scalePrecisionGrams: precision,
    displayDecimals: decimals,
    estimatedThcMg,
    targetThcMg: input.targetThcMg,
    differenceMg,
    absoluteDifferenceMg: Math.abs(differenceMg),
    percentDifference:
      input.targetThcMg > 0 ? (differenceMg / input.targetThcMg) * 100 : null,
    mode: 'closest',
    exactlyAchievable: Math.abs(differenceMg) <= 1e-6,
    reachability,
  };

  return {
    result,
    satisfiedMode: true,
    targetOutOfRange: false,
  };
}
